<template>
  <div class="error-editing-view">
    <cv-form>
      <!-- Question Body -->
      <match-indicator v-if="props.highlights" />
      <dl ref="container" class="field-set type-text question-body">
        <dd class="field-value">
          <rich-text-wrapper
            :content="questionBody"
            :expandable="true"
            :expanded="true"
            :can-annotate="canAccessAnnotations"
            :anchor="questionBodyAnchor"
            :resource-type="'LESSON_COMPONENT'"
            :annotation-resource-id="questionAnnotationResourceId"
          />
        </dd>
      </dl>

      <!-- Instructions -->
      <dl class="field-set type-text instructions">
        <dt class="field-label">
          <svg-icon name="Info16" kind="indicative" />
          <span>{{ t("question_component_label_instructions") }}</span>
        </dt>
        <dd class="field-value">
          {{ t("question_component_label_eeq_instructions_content") }}
        </dd>
      </dl>

      <!-- Hide No Error -->
      <dl
        class="field-set type-checkbox"
        :class="{ selected: props.question.allQuestionsContainError }"
      >
        <dd class="field-value">
          <span class="icon">
            <svg-icon
              v-if="props.question.allQuestionsContainError"
              name="Checkmark16"
              kind="indicative"
            />
          </span>
          {{ t("question_eeq_label_hide_no_error") }}
        </dd>
      </dl>

      <!-- Show Selected Errors -->
      <dl class="field-set type-checkbox" :class="{ selected: props.question.showSelectedErrors }">
        <dd class="field-value">
          <span class="icon">
            <svg-icon
              v-if="props.question.showSelectedErrors"
              name="Checkmark16"
              kind="indicative"
            />
          </span>
          {{ t("question_eeq_label_show_identified_error") }}
        </dd>
      </dl>

      <!-- All Case Sensitive -->
      <dl class="field-set type-checkbox" :class="{ selected: allCaseSensitive }">
        <dd class="field-value">
          <span class="icon">
            <svg-icon v-if="allCaseSensitive" name="Checkmark16" kind="indicative" />
          </span>
          <svg-icon name="LetterCase24" kind="action" />
          {{ t("question_eeq_label_answer_case_sensitive") }}
        </dd>
      </dl>

      <!-- Error Editing Answers -->
      <div class="field-group options">
        <dl
          v-for="(option, $index) in answerOptions"
          :key="$index"
          class="field-set type-text answers-set"
        >
          <error-editing-option-view :option="option" />
        </dl>
      </div>

      <!-- Hint -->
      <dl v-if="hint" class="field-set type-text hint">
        <dd class="field-value">
          <AccordionWrapper>
            <cv-accordion-item>
              <template #title>
                <span class="icon">
                  <svg-icon name="Hint16" kind="indicative" />
                </span>
                <span class="title">{{ t("question_component_label_hint") }}</span>
              </template>
              <template #content>
                <rich-text class="hint" :content="hint" :expandable="false" />
              </template>
            </cv-accordion-item>
          </AccordionWrapper>
        </dd>
      </dl>

      <div v-if="hint" class="divider" />

      <!-- Feedback(Explanation) -->
      <dl v-if="feedback" class="field-set type-text feedback">
        <dd class="field-value">
          <rich-text-card
            :title="t('question_component_explanation_label')"
            :content="feedback"
            truncation-type="secondary"
          >
            <template #icon><svg-icon name="Feedback32" kind="indicative" /></template>
          </rich-text-card>
        </dd>
      </dl>
    </cv-form>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAnnotationPermissions } from "@modules/annotation/composables/use-annotation-permissions.ts";
import { useQuestionAnnotation } from "@modules/editor/composables/use-question-annotation.ts";
import RichTextWrapper from "@/plugins/richtext/components/RichTextWrapper.vue";

const ErrorEditingOptionView = defineAsyncComponent(
  () => import("@plugins/common/components/ErrorEditingOptionView.vue"),
);

interface Question {
  questionBody: string;
  hint?: string;
  explanation?: string;
  questionOptionModels: {
    caseSensitive?: boolean;
  }[];
  allQuestionsContainError?: boolean;
  showSelectedErrors?: boolean;
  uuid: string;
}

interface Props {
  question: Question;
  highlights?: object;
}

const props = withDefaults(defineProps<Props>(), {
  highlights: undefined,
});

const emit = defineEmits<{
  loaded: [uuid: string];
}>();

const { t } = useI18n();
const { canAccessAnnotations } = useAnnotationPermissions();
const { questionBodyAnchor, questionAnnotationResourceId } = useQuestionAnnotation();

// Refs
const container = ref<HTMLElement | null>(null);

// Computed
const questionBody = computed(() => props.question.questionBody);
const hint = computed(() => props.question.hint);

const feedback = computed(() => props.question.explanation);

const answerOptions = computed(() => props.question.questionOptionModels);

const allCaseSensitive = computed(() =>
  answerOptions.value.every((answer) => answer.caseSensitive === true),
);

// Lifecycle
onMounted(() => {
  emit("loaded", props.question.uuid);
});
</script>

<style lang="scss">
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.error-editing-view {
  .field-set {
    &.type-checkbox {
      &.selected {
        .icon {
          background-color: $disabled-02;

          .sls-icon {
            [stroke] {
              stroke: $icon-03;
            }

            &[name="Checkmark16"] {
              [fill] {
                fill: $icon-03;
              }
            }
          }
        }
      }
      .field-value {
        display: flex;
        align-items: flex-end;
        color: $text-02;

        > .sls-icon {
          margin-right: $spacing-02;
        }
      }

      .icon {
        display: inline-flex;
        justify-content: center;
        align-items: center;
        flex: 0 0 1.25rem;
        width: 1.25rem;
        height: 1.25rem;
        border: 1px solid $disabled-02;
        border-radius: 5px;
        margin-right: $spacing-03;
      }
    }

    &.answers-set {
      border-top: 2px solid $ui-04;

      &:last-child {
        margin-bottom: 0;
      }

      .answer-option {
        counter-increment: line-counter;

        &:before {
          content: "(" counter(line-counter) ")";
          @include carbon--type-style("productive-heading-01", true);
          display: inline-block;
        }
      }
    }
  }

  .field-group {
    &.options {
      counter-reset: line-counter;
      border-bottom: 1px solid $ui-03;
      margin-bottom: $spacing-04;
    }
  }

  .error-editing-option-text {
    margin-top: 0;
  }
}
</style>
